/**
 * Doctor Search page — search by specialization, county, or name.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const kenyanCounties = [
  'Baringo','Bomet','Bungoma','Busia','Elgeyo-Marakwet','Embu','Garissa',
  'Homa Bay','Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi',
  'Kirinyaga','Kisii','Kisumu','Kitui','Kwale','Laikipia','Lamu','Machakos',
  'Makueni','Mandera','Marsabit','Meru','Migori','Mombasa',"Murang'a",
  'Nairobi','Nakuru','Nandi','Narok','Nyamira','Nyandarua','Nyeri','Samburu',
  'Siaya','Taita-Taveta','Tana River','Tharaka-Nithi','Trans-Nzoia','Turkana',
  'Uasin Gishu','Vihiga','Wajir','West Pokot'
];

const ALL_SPECIALIZATIONS = [
  'General Practice', 'Internal Medicine', 'Cardiology', 'Endocrinology',
  'Nephrology', 'Oncology', 'Pulmonology', 'Neurology', 'Psychiatry',
  'Pediatrics', 'Dermatology', 'Ophthalmology', 'Orthopedics',
  'Obstetrics & Gynecology', 'Surgery', 'Family Medicine', 'HIV/AIDS Specialist',
  'Diabetology', 'Rheumatology', 'Other',
];

// Maps symptom/condition keywords → best-matching specialization
const CONDITION_MAP = {
  'diabetes': 'Diabetology', 'blood sugar': 'Diabetology', 'insulin': 'Endocrinology',
  'thyroid': 'Endocrinology', 'weight': 'Endocrinology',
  'heart': 'Cardiology', 'chest pain': 'Cardiology', 'hypertension': 'Cardiology', 'blood pressure': 'Cardiology',
  'kidney': 'Nephrology', 'renal': 'Nephrology',
  'cancer': 'Oncology', 'tumor': 'Oncology',
  'lung': 'Pulmonology', 'asthma': 'Pulmonology', 'breathing': 'Pulmonology', 'cough': 'Pulmonology',
  'brain': 'Neurology', 'seizure': 'Neurology', 'stroke': 'Neurology', 'headache': 'Neurology',
  'mental': 'Psychiatry', 'depression': 'Psychiatry', 'anxiety': 'Psychiatry',
  'child': 'Pediatrics', 'infant': 'Pediatrics', 'baby': 'Pediatrics',
  'skin': 'Dermatology', 'rash': 'Dermatology', 'acne': 'Dermatology',
  'eye': 'Ophthalmology', 'vision': 'Ophthalmology', 'glasses': 'Ophthalmology',
  'bone': 'Orthopedics', 'fracture': 'Orthopedics', 'spine': 'Orthopedics',
  'joint': 'Rheumatology', 'arthritis': 'Rheumatology',
  'pregnancy': 'Obstetrics & Gynecology', 'antenatal': 'Obstetrics & Gynecology', 'gynecology': 'Obstetrics & Gynecology',
  'hiv': 'HIV/AIDS Specialist', 'aids': 'HIV/AIDS Specialist',
  'surgery': 'Surgery', 'operation': 'Surgery',
  'fever': 'General Practice', 'flu': 'General Practice', 'cold': 'General Practice',
};

const DoctorSearch = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ specialization: '', county: '', name: '', condition: '' });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [conditionSuggestion, setConditionSuggestion] = useState('');

  const handleConditionChange = (value) => {
    setFilters(f => ({ ...f, condition: value }));
    const lower = value.toLowerCase();
    const match = Object.entries(CONDITION_MAP).find(([kw]) => lower.includes(kw));
    setConditionSuggestion(match ? match[1] : '');
  };

  const applySuggestion = () => {
    setFilters(f => ({ ...f, specialization: conditionSuggestion, condition: '' }));
    setConditionSuggestion('');
  };

  const handleSearch = async () => {
    if (!filters.specialization && !filters.county && !filters.name && !filters.condition) {
      toast.error('Please enter at least one search criteria.');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await patientAPI.searchDoctors(filters);
      setResults(Array.isArray(res.data) ? res.data : (res.data?.doctors || []));
    } catch {
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Find a Doctor" role="patient" showBack>
      <div className="p-4 space-y-4 pb-24">
        {/* Search Filters */}
        <div className="card space-y-3">
          {/* Condition / symptom keyword field */}
          <div>
            <label className="label">Condition / Symptom</label>
            <input
              type="text"
              value={filters.condition}
              onChange={e => handleConditionChange(e.target.value)}
              className="input-field"
              placeholder="e.g. diabetes, chest pain, rash, pregnancy..."
            />
            {conditionSuggestion && (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-xs text-gray-500">Suggested:</span>
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full hover:bg-primary-100"
                >
                  {conditionSuggestion} →
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="label">Specialization</label>
            <select
              value={filters.specialization}
              onChange={e => setFilters(f => ({ ...f, specialization: e.target.value }))}
              className="input-field"
            >
              <option value="">All Specializations</option>
              {ALL_SPECIALIZATIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">County</label>
            <select value={filters.county} onChange={e => setFilters(f => ({ ...f, county: e.target.value }))} className="input-field">
              <option value="">All Counties</option>
              {kenyanCounties.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="label">Doctor Name</label>
            <input
              type="text"
              value={filters.name}
              onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
              className="input-field"
              placeholder="Search by name..."
            />
          </div>

          <button onClick={handleSearch} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading && <div className="spinner w-5 h-5 border-2"></div>}
            Search Doctors
          </button>
        </div>

        {/* Results */}
        {searched && (
          <div>
            <p className="text-sm text-gray-500 mb-3">{results.length} doctor{results.length !== 1 ? 's' : ''} found</p>

            {results.length === 0 ? (
              <div className="card text-center py-8 text-gray-400">
                <p className="text-sm">No doctors found matching your criteria.</p>
                <p className="text-xs mt-1">Try broadening your search.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map(doc => (
                  <div key={doc.id || doc.uid} className="card hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-accent-700 font-bold text-lg">
                          {(doc.full_name || 'D').charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">Dr. {doc.full_name}</p>
                        <p className="text-sm text-primary-600">{doc.specialization}</p>
                        {doc.services_offered?.length > 1 && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Also: {doc.services_offered.filter(s => s !== doc.specialization).join(', ')}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
                          {doc.county && <span>📍 {doc.county}</span>}
                          {doc.years_of_experience && <span>{doc.years_of_experience} yrs exp</span>}
                          {doc.available_days?.length > 0 && (
                            <span className="text-green-600">✓ {doc.available_days.slice(0,3).join(', ')}{doc.available_days.length > 3 ? '+' : ''}</span>
                          )}
                        </div>
                        {doc.hospital_affiliation && (
                          <p className="text-xs text-gray-400 mt-0.5">🏥 {doc.hospital_affiliation}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-900">KES {doc.consultation_fee || 500}</p>
                        <button
                          onClick={() => navigate(`/patient/doctors/${doc.uid || doc.id}`)}
                          className="mt-2 w-full px-3 py-1.5 bg-white text-primary-600 border border-primary-200 text-xs font-medium rounded-lg hover:bg-primary-50 transition-colors"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={() => navigate(`/patient/appointments/book?doctor=${doc.uid || doc.id}`)}
                          className="mt-2 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors"
                        >
                          Book
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorSearch;
